/* eslint-disable no-restricted-imports */
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { ContentHeader } from '@/components/business/ContentHeader';
import { DescriptionList } from '@/components/business/DescriptionList';
import { Wizard } from '@/components/business/Wizard';
import {
    AspectRatio,
    Backdrop,
    Background,
    Button,
    CardGroup,
    Container,
    ContentCard,
    Heading,
    ImageCard,
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
    ScrollArea,
    Separator,
    Skeleton,
    StatCard,
    Surface,
    Text
} from '@/components/ui';
import { SidebarMenu } from '@/components/ui/SidebarMenu';

import {
    createFamily,
    LabeledPreviewCell,
    LabeledPreviewGrid,
    PreviewRow,
    PreviewStack,
    PreviewSurface,
    StoryCard,
    VariantGallery,
    VariantPreview,
    withStorySources
} from './helpers';


const AspectRatioStory = () => (
    <PreviewStack>
        <VariantPreview meta={[{ label: 'ratio', value: '16/9 (video)' }]}>
            <AspectRatio x={16} y={9}>
                <Skeleton />
            </AspectRatio>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'ratio', value: '4/3 (standard)' }]}>
            <AspectRatio x={4} y={3}>
                <Skeleton />
            </AspectRatio>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'ratio', value: '1/1 (square)' }]}>
            <AspectRatio x={1} y={1}>
                <Skeleton />
            </AspectRatio>
        </VariantPreview>
    </PreviewStack>
);

const AspectRatioHero = () => (
    <AspectRatio x={16} y={9}>
        <Skeleton />
    </AspectRatio>
);

const CARD_TONE_LABELS = {
    neutral: 'Neutral',
    muted: 'Muted',
    primary: 'Primary',
    secondary: 'Secondary'
};

const CARD_TONES = ['neutral', 'muted', 'primary', 'secondary'];

const CARD_IMAGE_BACKGROUNDS = {
    neutral: 'tw-h-24 tw-w-full tw-bg-[linear-gradient(135deg,#224C4F,#8BC53F)]',
    muted: 'tw-h-24 tw-w-full tw-bg-[linear-gradient(135deg,#224C4F,#8BC53F)]',
    primary: 'tw-h-24 tw-w-full tw-bg-[linear-gradient(135deg,#0f172a,#224C4F)]',
    secondary: 'tw-h-24 tw-w-full tw-bg-[linear-gradient(135deg,#d9efb8,#8BC53F)]'
};

const CardStoryCopy = ({ children }) => (
    <div className="tw-text-sm tw-text-current tw-opacity-80">
        {children}
    </div>
);

const TYPOGRAPHY_HEADING_SAMPLES = [
    { label: 'display', sample: 'Catalog family heading', variant: 'display' },
    { label: 'title-lg', sample: 'Section heading', variant: 'title-lg' },
    { label: 'title', sample: 'Overlay title', variant: 'title' },
    { label: 'title-sm', sample: 'Card heading', variant: 'title-sm' }
];

const TYPOGRAPHY_TEXT_SAMPLES = [
    { label: 'overline', sample: 'UI Catalog', variant: 'overline' },
    { label: 'caption', sample: 'Caption sized text for annotations and dense UI chrome.', variant: 'caption' },
    { label: 'body-sm', sample: 'Dense body copy for compact rows and helper text.', variant: 'body-sm' },
    { label: 'body', sample: 'Default app body copy for descriptions, form help text, and inline documentation.', variant: 'body' },
    { label: 'control', sample: 'Interactive control copy for labels, menus, and breadcrumb links.', variant: 'control' }
];

const TYPOGRAPHY_TONE_SAMPLES = [
    { tone: 'foreground', label: 'foreground (default)', sample: 'Primary copy on the standard surface.' },
    { tone: 'muted', label: 'muted', sample: 'Secondary copy for descriptions and meta data.' },
    { tone: 'background', label: 'background', sample: 'Inverted copy for solid-colour surfaces.' },
    { tone: 'info', label: 'info', sample: 'Informational accent — use to highlight neutral status copy.' },
    { tone: 'success', label: 'success', sample: 'Positive feedback — confirmations and success states.' },
    { tone: 'warning', label: 'warning', sample: 'Cautionary feedback — non-blocking heads-ups.' },
    { tone: 'destructive', label: 'destructive', sample: 'Error feedback — validation messages and failures.' },
    { tone: 'inherit', label: 'inherit', sample: 'No colour applied — inherits from the surrounding context.' }
];

const TypographyHero = () => (
    <div className="tw-space-y-3">
        <Heading variant="title">Section heading</Heading>
        <Text as="p" variant="body">Default app body copy for descriptions, form help text, and inline documentation.</Text>
        <Text as="p" tone="muted" variant="body-sm">Dense supporting copy for compact rows and helper text.</Text>
    </div>
);

const TypographyStory = () => (
    <PreviewStack>
        <VariantPreview meta={[{ label: 'exports', value: 'Heading + Text' }, { label: 'axis', value: 'variants' }]}>
            <div className="tw-grid tw-gap-6 lg:tw-grid-cols-2">
                <div className="tw-space-y-4">
                    {TYPOGRAPHY_HEADING_SAMPLES.map(sample => (
                        <div key={sample.variant} className="tw-space-y-1">
                            <Text as="p" tone="muted" variant="overline">{sample.label}</Text>
                            <Heading variant={sample.variant}>{sample.sample}</Heading>
                        </div>
                    ))}
                </div>
                <div className="tw-space-y-4">
                    {TYPOGRAPHY_TEXT_SAMPLES.map(sample => (
                        <div key={sample.variant} className="tw-space-y-1">
                            <Text as="p" tone="muted" variant="overline">{sample.label}</Text>
                            <Text as="p" variant={sample.variant}>{sample.sample}</Text>
                        </div>
                    ))}
                </div>
            </div>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'axis', value: 'tones' }]}>
            <div className="tw-space-y-3">
                {TYPOGRAPHY_TONE_SAMPLES.map(sample => (
                    <div key={sample.tone} className="tw-grid tw-grid-cols-[8rem_1fr] tw-items-baseline tw-gap-4">
                        <Text tone="muted" variant="overline">{sample.label}</Text>
                        {sample.tone === 'background' ? (
                            <div className="tw-rounded-md tw-bg-foreground tw-px-3 tw-py-2">
                                <Text tone="background" variant="body">{sample.sample}</Text>
                            </div>
                        ) : (
                            <Text tone={sample.tone} variant="body">{sample.sample}</Text>
                        )}
                    </div>
                ))}
            </div>
        </VariantPreview>
    </PreviewStack>
);

const CARD_STORY_SURFACE_CLASSNAME = 'tw-min-h-0 tw-items-stretch tw-justify-start';

const renderContentCardPreview = ({ example, tone }) => {
    const isHoverable = example === 'hoverable';
    const toneLabel = CARD_TONE_LABELS[tone];
    const meta = [
        { label: 'component', value: 'ContentCard' },
        { label: 'tone', value: tone },
        { label: 'example', value: example }
    ];

    return (
        <VariantPreview key={`content-${tone}-${example}`} className={CARD_STORY_SURFACE_CLASSNAME} meta={meta}>
            <ContentCard
                description={`${toneLabel} card in ${example} mode`}
                interactive={isHoverable}
                onClick={isHoverable ? () => undefined : undefined}
                primaryAction={{ label: 'Action' }}
                title={`${toneLabel} content card`}
                tone={tone}
            >
                <CardStoryCopy>
                    The body copy should inherit the card language instead of defining a separate palette.
                </CardStoryCopy>
            </ContentCard>
        </VariantPreview>
    );
};

const renderImageCardPreview = ({ example, tone }) => {
    const isHoverable = example === 'hoverable';
    const toneLabel = CARD_TONE_LABELS[tone];
    const meta = [
        { label: 'component', value: 'ImageCard' },
        { label: 'tone', value: tone },
        { label: 'example', value: example }
    ];

    return (
        <VariantPreview key={`image-${tone}-${example}`} className={CARD_STORY_SURFACE_CLASSNAME} meta={meta}>
            <ImageCard
                image={<div className={CARD_IMAGE_BACKGROUNDS[tone]} />}
                interactive={isHoverable}
                onClick={isHoverable ? () => undefined : undefined}
                primaryAction={{ label: 'Choose' }}
                title={`${toneLabel} image card`}
                tone={tone}
            >
                <CardStoryCopy>
                    Hover stays owned by the card contract while selection logic remains entirely outside the component.
                </CardStoryCopy>
            </ImageCard>
        </VariantPreview>
    );
};

const renderCardActionsPreview = ({ component, tone }) => {
    const Component = component === 'ImageCard' ? ImageCard : ContentCard;
    const toneLabel = CARD_TONE_LABELS[tone];
    const sharedProps = component === 'ImageCard'
        ? { image: <div className={CARD_IMAGE_BACKGROUNDS.primary} /> }
        : {};

    return (
        <VariantPreview
            key={`${component}-${tone}-actions`}
            className={CARD_STORY_SURFACE_CLASSNAME}
            meta={[
                { label: 'component', value: component },
                { label: 'tone', value: tone },
                { label: 'actions', value: 'primary + secondary' }
            ]}
        >
            <Component
                {...sharedProps}
                description={`${toneLabel} card with both action slots populated`}
                primaryAction={{ label: 'Continue' }}
                secondaryAction={{ label: 'Details' }}
                title={`${toneLabel} actions`}
                tone={tone}
            >
                <CardStoryCopy>
                    The card owns CTA layout and contrast when both action slots are present.
                </CardStoryCopy>
            </Component>
        </VariantPreview>
    );
};

const renderStatCardPreview = tone => (
    <VariantPreview
        key={`stat-${tone}`}
        className={CARD_STORY_SURFACE_CLASSNAME}
        meta={[
            { label: 'component', value: 'StatCard' },
            { label: 'tone', value: tone }
        ]}
    >
        <StatCard title={`${CARD_TONE_LABELS[tone]} metric`} value="12" tone={tone} />
    </VariantPreview>
);

const CardStory = () => {
    const contentCardPreviews = CARD_TONES.flatMap(tone => (
        ['resting', 'hoverable'].map(example => renderContentCardPreview({ example, tone }))
    ));
    const imageCardPreviews = CARD_TONES.flatMap(tone => (
        ['resting', 'hoverable'].map(example => renderImageCardPreview({ example, tone }))
    ));
    const statCardPreviews = CARD_TONES.map(renderStatCardPreview);
    const contentCardActionPreviews = CARD_TONES.map(tone => (
        renderCardActionsPreview({ component: 'ContentCard', tone })
    ));
    const imageCardActionPreviews = CARD_TONES.map(tone => (
        renderCardActionsPreview({ component: 'ImageCard', tone })
    ));

    return (
        <PreviewStack>
            <StoryCard
                description="Dense audit grid for the content variant family. The component owns hover; callers own selection logic by deciding which variant to render."
                title="ContentCard"
            >
                <VariantGallery columns={2}>
                    {contentCardPreviews}
                </VariantGallery>
                <VariantGallery className="tw-mt-4" columns={2}>
                    {contentCardActionPreviews}
                </VariantGallery>
            </StoryCard>

            <StoryCard
                description="Image cards follow the same variant contract as content cards, without any extra card-level state prop."
                title="ImageCard"
            >
                <VariantGallery columns={2}>
                    {imageCardPreviews}
                </VariantGallery>
                <VariantGallery className="tw-mt-4" columns={2}>
                    {imageCardActionPreviews}
                </VariantGallery>
            </StoryCard>

            <StoryCard
                description="Supporting card primitives stay in the same family without needing extra editorial chrome."
                title="StatCard and CardGroup"
            >
                <VariantGallery>
                    {statCardPreviews}
                    <VariantPreview className={CARD_STORY_SURFACE_CLASSNAME} meta={[{ label: 'component', value: 'CardGroup' }]}>
                        <CardGroup columns={3}>
                            <StatCard title="One" value="12" />
                            <StatCard title="Two" value="18" />
                            <StatCard title="Three" value="24" />
                        </CardGroup>
                    </VariantPreview>
                </VariantGallery>
            </StoryCard>

        </PreviewStack>
    );
};

const CardHero = () => (
    <ContentCard
        description="Card with both action slots populated and a feature-rich body."
        primaryAction={{ label: 'Continue' }}
        secondaryAction={{ label: 'Details' }}
        title="Primary content card"
        tone="primary"
    >
        ContentCard owns padding, hover, and CTA layout. Pick the tone that matches the surface intent.
    </ContentCard>
);

const ContainerHero = () => (
    <Background heightOffset={720}>
        <Container variant="column">
            <div className="tw-rounded-xl tw-border tw-border-dashed tw-border-border tw-bg-muted tw-p-6 tw-text-foreground tw-text-sm">
                Background and Container define the outer frame for standalone pages.
            </div>
        </Container>
    </Background>
);

const DescriptionListHero = () => (
    <DescriptionList items={[
        { label: 'ID', value: 'a8f33caa-dd9e-48ae-a558-32c744355b81' },
        { label: 'Name', value: 'Field service integration' },
        { label: 'Token', value: 'wm_live_AfRkP9b3J2lN1cQz0LtH4eEs' },
        { label: 'Rate', value: '900 (15 minutes) · legacy rate', tone: 'destructive' },
        { label: 'Groups', value: 'Operations, Field' },
        { label: 'Sites', value: 'Lisboa HQ, Porto Annex, Coimbra Office' }
    ]}/>
);

const DescriptionListStory = () => (
    <PreviewStack>
        <VariantPreview meta={[{ label: 'shape', value: 'six entries' }]} padding={false}>
            <div className="tw-w-full tw-rounded-2xl tw-border tw-border-border tw-bg-background tw-p-6">
                <DescriptionList items={[
                    { label: 'ID', value: 'a8f33caa-dd9e-48ae-a558-32c744355b81' },
                    { label: 'Name', value: 'Field service integration' },
                    { label: 'Token', value: 'wm_live_AfRkP9b3J2lN1cQz0LtH4eEs' },
                    { label: 'Rate', value: '3600 (1 hour)' },
                    { label: 'Groups', value: 'Operations, Field' },
                    { label: 'Sites', value: 'Lisboa HQ, Porto Annex' }
                ]}/>
            </div>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'tone', value: 'destructive on a row' }]} padding={false}>
            <div className="tw-w-full tw-rounded-2xl tw-border tw-border-border tw-bg-background tw-p-6">
                <DescriptionList items={[
                    { label: 'Name', value: 'Legacy export job' },
                    { label: 'Rate', value: '900 (15 minutes) · legacy rate', tone: 'destructive' },
                    { label: 'Last run', value: '2026-05-30 14:00' }
                ]}/>
            </div>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'value', value: 'empty → em-dash' }]} padding={false}>
            <div className="tw-w-full tw-rounded-2xl tw-border tw-border-border tw-bg-background tw-p-6">
                <DescriptionList items={[
                    { label: 'Name', value: 'Brand new tag' },
                    { label: 'Description', value: null },
                    { label: 'Groups', value: '' }
                ]}/>
            </div>
        </VariantPreview>
    </PreviewStack>
);

const ContentHeaderHero = () => (
    <ContentHeader
        description="Primary is the default — icon without background."
        icon="globe"
        title="Catalog section"
    >
        <Button size="small">Primary action</Button>
        <Button size="small" appearance="outline" tone="neutral">Secondary</Button>
    </ContentHeader>
);

const ContentHeaderStory = () => (
    <PreviewStack>
        <VariantPreview meta={[{ label: 'description', value: 'present' }]} className="tw-items-stretch" padding={false}>
            <div className="tw-rounded-2xl tw-border tw-border-border tw-bg-background tw-p-6">
                <ContentHeader
                    description="Primary is the default — icon without background."
                    icon="globe"
                    title="Catalog section"
                >
                    <Button size="small">Primary action</Button>
                    <Button size="small" appearance="outline" tone="neutral">Secondary</Button>
                </ContentHeader>
            </div>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'variant', value: 'secondary' }]} className="tw-items-stretch" padding={false}>
            <div className="tw-rounded-2xl tw-border tw-border-border tw-bg-background tw-p-6">
                <ContentHeader
                    description="Secondary tone paints the icon in the secondary brand colour."
                    icon="alarm"
                    title="Alarms"
                    tone="secondary"
                >
                    <Button size="small" tone="secondary">Edit</Button>
                </ContentHeader>
            </div>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'description', value: 'absent' }]} className="tw-items-stretch" padding={false}>
            <div className="tw-rounded-2xl tw-border tw-border-border tw-bg-background tw-p-6">
                <ContentHeader
                    icon="alarm"
                    title="Alarms"
                />
            </div>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'variant', value: 'secondary' }, { label: 'description', value: 'absent' }]} className="tw-items-stretch" padding={false}>
            <div className="tw-rounded-2xl tw-border tw-border-border tw-bg-background tw-p-6">
                <ContentHeader
                    icon="globe"
                    title="Language"
                    tone="secondary"
                />
            </div>
        </VariantPreview>
    </PreviewStack>
);

const ResizableHero = () => (
    <div className="tw-h-64 tw-w-full">
        <ResizablePanelGroup direction="horizontal">
            <ResizablePanel size={30} minSize={20}>
                <Surface fullHeight tone="neutral">
                    <Heading as="h3" variant="title-sm">Sidebar</Heading>
                    <Separator />
                    <Text tone="muted" variant="body-sm">Filters, tools, navigation.</Text>
                </Surface>
            </ResizablePanel>
            <ResizableHandle>
                <Separator orientation="vertical" />
            </ResizableHandle>
            <ResizablePanel size={70} minSize={50}>
                <Surface fullHeight tone="neutral">
                    <Heading as="h3" variant="title-sm">Main content</Heading>
                    <Separator />
                    <Text tone="muted" variant="body-sm">Drag the handle to resize. The `minSize` of each panel constrains the layout.</Text>
                </Surface>
            </ResizablePanel>
        </ResizablePanelGroup>
    </div>
);

const ScrollAreaHero = () => (
    <div className="tw-h-40 tw-w-full tw-rounded-xl tw-border tw-border-border tw-bg-background tw-p-4">
        <ScrollArea>
            <div className="tw-space-y-2">
                {Array.from({ length: 12 }, (_, index) => (
                    <div
                        className="tw-rounded-lg tw-border tw-border-border tw-bg-muted tw-p-3"
                        key={index}
                    >
                        <Text variant="body">
                            Scrollable row {index + 1}
                        </Text>
                    </div>
                ))}
            </div>
        </ScrollArea>
    </div>
);

const SeparatorHero = () => (
    <div className="tw-w-full">
        <Text variant="body">Before separator</Text>
        <div className="tw-my-4">
            <Separator />
        </div>
        <Text variant="body">After separator</Text>
    </div>
);

const SeparatorStory = () => (
    <PreviewStack>
        <VariantPreview meta={[{ label: 'orientation', value: 'horizontal' }]}>
            <div className="tw-w-full">
                <Text variant="body">Before separator</Text>
                <div className="tw-my-4">
                    <Separator />
                </div>
                <Text variant="body">After separator</Text>
            </div>
        </VariantPreview>
        <VariantPreview meta={[{ label: 'orientation', value: 'vertical' }]}>
            <div className="tw-flex tw-h-16 tw-items-center">
                <Text variant="body">Left</Text>
                <div className="tw-mx-4 tw-h-full">
                    <Separator orientation="vertical" />
                </div>
                <Text variant="body">Right</Text>
            </div>
        </VariantPreview>
    </PreviewStack>
);

const SidebarMenuHero = () => {
    const items = [
        { value: 'profile', label: 'Profile', icon: 'user' },
        { value: 'password', label: 'Password', icon: 'password' },
        { value: 'notifications', label: 'Notifications', icon: 'notifications' },
        { value: 'language', label: 'Language', icon: 'globe' }
    ];
    const [value, setValue] = React.useState('profile');

    return (
        <div className="tw-flex tw-w-full tw-gap-6">
            <div className="tw-basis-1/4 tw-shrink-0">
                <SidebarMenu items={items} onChange={setValue} value={value} />
            </div>
            <div className="tw-basis-3/4 tw-flex tw-items-center tw-justify-center">
                <Text tone="muted" variant="body">
                    Selected: <Text as="span" tone="foreground" variant="control">{value}</Text>
                </Text>
            </div>
        </div>
    );
};

const SidebarMenuStory = () => {
    const items = [
        { value: 'overview', label: 'Overview', icon: 'chartMixed', description: '12 exports · 34 total' },
        { value: 'examples', label: 'Examples', icon: 'bookmark', description: '8 exports · 21 total' },
        { value: 'usage', label: 'Usage', icon: 'circleInfo', description: '14 exports · 41 total' }
    ];
    const [mutedValue, setMutedValue] = React.useState('examples');
    const [primaryValue, setPrimaryValue] = React.useState('examples');

    return (
        <PreviewStack>
            <VariantPreview meta={[{ label: 'tone', value: 'muted (default)' }]}>
                <div className="tw-flex tw-w-full tw-gap-6">
                    <div className="tw-basis-1/4 tw-shrink-0">
                        <SidebarMenu items={items} onChange={setMutedValue} value={mutedValue} />
                    </div>
                    <div className="tw-basis-3/4 tw-flex tw-items-center tw-justify-center">
                        <Text tone="muted" variant="body">
                            Selected: <Text as="span" tone="foreground" variant="control">{mutedValue}</Text>
                        </Text>
                    </div>
                </div>
            </VariantPreview>
            <VariantPreview meta={[{ label: 'tone', value: 'primary' }]}>
                <div className="tw-flex tw-w-full tw-gap-6">
                    <div className="tw-basis-1/4 tw-shrink-0">
                        <SidebarMenu items={items} onChange={setPrimaryValue} tone="primary" value={primaryValue} />
                    </div>
                    <div className="tw-basis-3/4 tw-flex tw-items-center tw-justify-center">
                        <Text tone="muted" variant="body">
                            Selected: <Text as="span" tone="foreground" variant="control">{primaryValue}</Text>
                        </Text>
                    </div>
                </div>
            </VariantPreview>
        </PreviewStack>
    );
};

const DemoStep = ({ label }) => <Text variant="body">{label}</Text>;

const WizardHero = () => {
    const form = useForm({ defaultValues: {} });

    const steps = [
        { Component: () => <DemoStep label="Step 1"/>, title: 'First step', description: 'Fill in the first step.' },
        { Component: () => <DemoStep label="Step 2"/>, title: 'Second step', description: 'Fill in the second step.' },
        { Component: () => <DemoStep label="Step 3"/>, title: 'Third step', description: 'Fill in the third step.' }
    ];

    const successStep = { Component: () => <Text variant="body">Completed</Text>, title: 'Done', description: 'All steps finished.' };

    return (
        <Wizard
            form={form}
            onCancel={() => alert('Wizard cancelled')}
            onClose={() => alert('Wizard finished!')}
            onSubmit={() => Promise.resolve()}
            steps={steps}
            successStep={successStep}
        />
    );
};

const SURFACE_TONES = ['neutral', 'muted', 'primary', 'secondary', 'info', 'success', 'warning', 'destructive'];

const SurfaceStory = () => (
    <PreviewStack>
        <StoryCard
            description="Eight tones — four brand (neutral / muted / primary / secondary) and four status (info / success / warning / destructive). Same paint, same shape; pick whatever expresses the surrounding meaning."
            title="Tones"
        >
            <VariantGallery columns={2}>
                {SURFACE_TONES.map(tone => (
                    <VariantPreview key={`surface-tone-${tone}`} meta={[{ label: 'tone', value: tone }]}>
                        <Surface tone={tone}>
                            <Text tone="inherit" variant="body">{tone}</Text>
                        </Surface>
                    </VariantPreview>
                ))}
            </VariantGallery>
        </StoryCard>

        <StoryCard
            description="`rounded` picks the corner radius to match the neighbouring control family. `small` (6px) matches Input / Select / TextArea; `medium` (8px) matches Button / IconButton (e.g. SummaryRow under the indicator plot, where the row sits beside the remove IconButton); `large` (12px, default) matches ContentCard / Dialog / Popover — the softer container feel. Composers like Card pin this prop so the radius is recorded at the composition boundary, not inherited tacitly."
            title="Rounded"
        >
            <VariantGallery columns={3}>
                <VariantPreview meta={[{ label: 'rounded', value: 'small (6px)' }]}>
                    <Surface rounded="small" tone="muted">
                        <Text tone="inherit" variant="body">Matches Input / Select / TextArea.</Text>
                    </Surface>
                </VariantPreview>
                <VariantPreview meta={[{ label: 'rounded', value: 'medium (8px)' }]}>
                    <Surface rounded="medium" tone="muted">
                        <Text tone="inherit" variant="body">Matches Button / IconButton.</Text>
                    </Surface>
                </VariantPreview>
                <VariantPreview meta={[{ label: 'rounded', value: 'large (12px, default)' }]}>
                    <Surface rounded="large" tone="muted">
                        <Text tone="inherit" variant="body">Card / Dialog / Popover default — softer container feel.</Text>
                    </Surface>
                </VariantPreview>
            </VariantGallery>
        </StoryCard>

        <StoryCard
            description="`padded` (default true) bakes 16px of inner padding. Set `padded={false}` for full-bleed content like images, tables or wizards that bring their own padding."
            title="Padding"
        >
            <VariantGallery columns={2}>
                <VariantPreview meta={[{ label: 'padded', value: 'true (default)' }]}>
                    <Surface tone="muted">
                        <Text tone="inherit" variant="body">Inherits the canonical p-4 ring around content.</Text>
                    </Surface>
                </VariantPreview>
                <VariantPreview meta={[{ label: 'padded', value: 'false' }]}>
                    <Surface padded={false} tone="muted">
                        <Text tone="inherit" variant="body">Edge-to-edge content. Consumer paints its own padding inside.</Text>
                    </Surface>
                </VariantPreview>
            </VariantGallery>
        </StoryCard>

        <StoryCard
            description="`border` (default true) draws the 1px solid border in the matching tone. Drop it for tinted boxes that should sit flush against an existing chrome."
            title="Border"
        >
            <VariantGallery columns={2}>
                <VariantPreview meta={[{ label: 'border', value: 'true (default)' }]}>
                    <Surface tone="info">
                        <Text tone="inherit" variant="body">Border in the matching tone.</Text>
                    </Surface>
                </VariantPreview>
                <VariantPreview meta={[{ label: 'border', value: 'false' }]}>
                    <Surface border={false} tone="info">
                        <Text tone="inherit" variant="body">No outline.</Text>
                    </Surface>
                </VariantPreview>
            </VariantGallery>
        </StoryCard>

        <StoryCard
            description="`interactive` adds focus / hover affordances; pair with `onClick` (or `href`) to make it a real action target. `disabled` dims and ignores pointer events."
            title="Interactive states"
        >
            <VariantGallery columns={2}>
                <VariantPreview meta={[{ label: 'state', value: 'interactive' }]}>
                    <Surface interactive onClick={() => undefined}>
                        <Text variant="body">Click and focus styling come for free when interactive is true.</Text>
                    </Surface>
                </VariantPreview>
                <VariantPreview meta={[{ label: 'state', value: 'disabled' }]}>
                    <Surface disabled interactive onClick={() => undefined}>
                        <Text variant="body">Disabled surfaces dim and ignore pointer events.</Text>
                    </Surface>
                </VariantPreview>
            </VariantGallery>
        </StoryCard>

        <StoryCard
            description="`fullHeight` applies `tw-h-full` so the Surface fills its parent — useful when the parent has a defined height (dashboard cells, grid items). Default natural height paints to content."
            title="fullHeight"
        >
            <VariantGallery columns={2}>
                <VariantPreview meta={[{ label: 'fullHeight', value: 'false (default)' }]}>
                    <div className="tw-flex tw-h-40 tw-flex-col">
                        <Surface tone="muted">
                            <Text tone="inherit" variant="body">Natural height — paints to content.</Text>
                        </Surface>
                    </div>
                </VariantPreview>
                <VariantPreview meta={[{ label: 'fullHeight', value: 'true' }]}>
                    <div className="tw-flex tw-h-40 tw-flex-col">
                        <Surface fullHeight tone="muted">
                            <Text tone="inherit" variant="body">Fills the 160px parent.</Text>
                        </Surface>
                    </div>
                </VariantPreview>
            </VariantGallery>
        </StoryCard>
    </PreviewStack>
);

const SurfaceHero = () => (
    <Surface tone="info">
        <div className="tw-flex tw-flex-col tw-gap-3">
            <Heading as="h5" tone="inherit" variant="title-sm">Password requirements</Heading>
            <Text tone="inherit" variant="body-sm">At least 8 characters.</Text>
            <Text tone="inherit" variant="body-sm">Includes a number.</Text>
            <Text tone="inherit" variant="body-sm">Includes a symbol.</Text>
        </div>
    </Surface>
);

const BackdropHero = () => {
    const [openDefault, setOpenDefault] = React.useState(false);

    return (
        <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-flex">
                <Button size="small" onClick={() => setOpenDefault(value => !value)}>
                    {openDefault ? 'Hide scrim' : 'Show scrim'}
                </Button>
            </div>
            <div className="tw-relative tw-h-40 tw-w-full tw-overflow-hidden tw-rounded-xl">
                <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-p-4">
                    <Text variant="body">Page content</Text>
                    <Button size="small" appearance="outline" tone="neutral">Action</Button>
                </div>
                {openDefault ? <Backdrop /> : null}
            </div>
        </div>
    );
};

const BackdropStory = () => {
    const [openDefault, setOpenDefault] = React.useState(false);
    const [openFull, setOpenFull] = React.useState(false);

    return (
        <PreviewStack>
            <section className="tw-space-y-3">
                <Text tone="muted" variant="overline">
                    Default — absolute scrim overlaying the closest positioned ancestor with the soft overlay tint. Use it as a decorative scrim when you only need to dim a contained surface (no portal, no focus trap, no escape).
                </Text>
                <PreviewSurface>
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        <div className="tw-flex">
                            <Button size="small" onClick={() => setOpenDefault(value => !value)}>
                                {openDefault ? 'Hide scrim' : 'Show scrim'}
                            </Button>
                        </div>
                        <div className="tw-relative tw-h-40 tw-w-full tw-overflow-hidden tw-rounded-xl">
                            <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-p-4">
                                <Text variant="body">Page content</Text>
                                <Button size="small" appearance="outline" tone="neutral">Action</Button>
                            </div>
                            {openDefault ? <Backdrop /> : null}
                        </div>
                    </div>
                </PreviewSurface>
            </section>

            <section className="tw-space-y-3">
                <Text tone="muted" variant="overline">
                    Full — viewport-fixed strong overlay with the modal stack (portal, focus trap, escape, click-outside). Dialog wraps this internally.
                </Text>
                <PreviewSurface>
                    <Button onClick={() => setOpenFull(true)}>Open full backdrop</Button>
                </PreviewSurface>
            </section>

            {openFull ? (
                <Backdrop full onClose={() => setOpenFull(false)}>
                    <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
                        <Button onClick={() => setOpenFull(false)}>Close</Button>
                    </div>
                </Backdrop>
            ) : null}
        </PreviewStack>
    );
};

const layoutStorySections = [
    createFamily({
        id: 'backdrop',
        label: 'Backdrop',
        description: 'Canonical overlay primitive. Default fills its parent with a soft scrim; `full` adds the modal stack (portal, viewport-fixed, focus trap, escape, click-outside) and a stronger dim. Dialog wraps the `full` shape internally.',
        subcomponents: ['Backdrop'],
        stories: [{ id: 'backdrop-shapes', title: 'Default and full', render: BackdropStory, hero: BackdropHero }]
    }),
    createFamily({
        id: 'aspect-ratio',
        label: 'AspectRatio',
        description: 'Keeps media and placeholders inside fixed aspect ratios.',
        subcomponents: ['AspectRatio'],
        stories: [{ id: 'aspect-ratio-basic', title: 'Common ratios', render: AspectRatioStory, hero: AspectRatioHero }]
    }),
    createFamily({
        id: 'card',
        label: 'Card',
        description: 'Opinionated card compositions over Surface — ContentCard / ImageCard / StatCard share the same header + body + actions + footer slots; CardGroup lays them out in a responsive grid.',
        subcomponents: ['ContentCard', 'ImageCard', 'StatCard', 'CardGroup'],
        stories: [{ id: 'card-variants', title: 'Content, image, stat and grouped cards', render: CardStory, hero: CardHero }]
    }),
    createFamily({
        id: 'surface',
        label: 'Surface',
        description: 'Bottom-of-stack paint primitive — tinted box with tone, optional border, optional padding, optional interactive affordances. Card family, dashboard widgets, info boxes (e.g. PasswordSetup) all compose Surface.',
        subcomponents: ['Surface'],
        stories: [{ id: 'surface-tones', title: 'Tones, border, padding and interactive states', render: SurfaceStory, hero: SurfaceHero }]
    }),
    createFamily({
        id: 'container',
        label: 'Container',
        description: 'Page-level background and width-constrained container wrappers.',
        subcomponents: ['Background', 'Container'],
        stories: [{ id: 'container-layout', title: 'Page frame', hero: ContainerHero }]
    }),
    createFamily({
        id: 'content-header',
        label: 'ContentHeader',
        layer: 'business',
        description: 'Compact section header with optional icon and action slot.',
        subcomponents: ['ContentHeader'],
        stories: [{ id: 'content-header-basic', title: 'Header with action', render: ContentHeaderStory, hero: ContentHeaderHero }]
    }),
    createFamily({
        id: 'description-list',
        label: 'DescriptionList',
        layer: 'business',
        description: 'Declarative key/value list for read-only detail surfaces (typically the body of a Show dialog).',
        subcomponents: ['DescriptionList'],
        stories: [{ id: 'description-list-shape', title: 'Label + value rows', render: DescriptionListStory, hero: DescriptionListHero }]
    }),
    createFamily({
        id: 'typography',
        label: 'Typography',
        description: 'Shared app text scale for headings, labels, body copy, and metrics.',
        subcomponents: ['Heading', 'Text'],
        stories: [{ id: 'typography-scale', title: 'Shared text scale', render: TypographyStory, hero: TypographyHero }]
    }),
    createFamily({
        id: 'resizable',
        label: 'Resizable',
        description: 'Panel group and resize handles for split layouts.',
        subcomponents: ['ResizablePanelGroup', 'ResizablePanel', 'ResizableHandle'],
        stories: [{ id: 'resizable-horizontal', title: 'Horizontal split', hero: ResizableHero }]
    }),
    createFamily({
        id: 'scroll-area',
        label: 'ScrollArea',
        description: 'Scrollable region with optional custom scrollbar.',
        subcomponents: ['ScrollArea'],
        stories: [{ id: 'scroll-area-basic', title: 'Scrollable list', hero: ScrollAreaHero }]
    }),
    createFamily({
        id: 'separator',
        label: 'Separator',
        description: 'Horizontal and vertical separators.',
        subcomponents: ['Separator'],
        stories: [{ id: 'separator-directions', title: 'Directions', render: SeparatorStory, hero: SeparatorHero }]
    }),
    createFamily({
        id: 'sidebar-menu',
        label: 'SidebarMenu',
        description: 'Vertical nav primitive with two tones — `muted` (default, flat row with subtle active state) and `primary` (card-style row with strong primary active state). Tone controls only paint; the wrapper is a minimal `flex-col gap-1` list either way. Caller owns any surrounding column layout (width, dividers, padding).',
        subcomponents: ['SidebarMenu'],
        stories: [{ id: 'sidebar-menu-basic', title: 'Vertical nav with two tones', render: SidebarMenuStory, hero: SidebarMenuHero }]
    }),
    createFamily({
        id: 'wizard',
        label: 'Wizard',
        layer: 'business',
        description: 'Multi-step flow with progress tracking, navigation, and submission.',
        subcomponents: ['Wizard'],
        stories: [{ id: 'wizard-overview', title: 'Multi-step flow', hero: WizardHero }]
    })
].map(withStorySources);

export { layoutStorySections };
